import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { findNextChallengeExercise, useChallengeExercises } from '../features/leaderboard/useChallengeExercises';
import type { CompetitionState } from '../features/leaderboard/leaderboardApi';
import './ChallengeStartPanel.css';

interface ChallengeStartPanelProps {
  courseId: string;
  /** Polled competition state: the panel only shows while a challenge is active, and reloads on RESET (new `startedAt`). */
  state: CompetitionState | undefined;
}

/** Student-side entry point into a live challenge: progress plus "start"/"continue" (leaderboard page). */
function ChallengeStartPanel({ courseId, state }: ChallengeStartPanelProps) {
  const { t } = useTranslation();
  const isActive = Boolean(state?.active);
  const challenge = useChallengeExercises(isActive ? courseId : null, state?.startedAt);

  if (!isActive || !challenge) {
    return null;
  }

  const total = challenge.exercises.length;
  const passed = challenge.exercises.filter((exercise) => exercise.passedInChallenge).length;
  const next = findNextChallengeExercise(challenge.exercises);

  function renderAction() {
    if (total === 0) {
      return <p>{t('leaderboard.challengeEmpty')}</p>;
    }
    if (!next) {
      return <p className="challenge-start__complete">{t('leaderboard.challengeComplete')}</p>;
    }
    return (
      <Link className="challenge-start__button" to={`/exercise/${next.id}?challenge=${courseId}`}>
        {passed === 0 ? t('leaderboard.startChallenge') : t('leaderboard.continueChallenge')}
      </Link>
    );
  }

  return (
    <section className="challenge-start" aria-label={t('leaderboard.startChallenge')}>
      <p className="challenge-start__progress">
        {t('leaderboard.challengeProgress', { chapter: challenge.chapterTitle, passed, total })}
      </p>
      {renderAction()}
    </section>
  );
}

export default ChallengeStartPanel;
